/**
 * NonEmpty helpers (W1-01 core-time-math). The RPE IR uses `NonEmpty<T>` so that `[]` event lists cannot be
 * represented (rpe-format.md F§0 rule 3); these helpers build and transform such lists without casts at call sites.
 */

import { AssertionError } from './assert';
import type { NonEmpty } from './types';

/** Type guard: the list has at least one element. */
export function isNonEmpty<T>(arr: readonly T[]): arr is NonEmpty<T> {
  return arr.length > 0;
}

/** The list as NonEmpty, or null when it is empty. */
export function toNonEmpty<T>(arr: readonly T[]): NonEmpty<T> | null {
  return isNonEmpty(arr) ? arr : null;
}

/** The list as NonEmpty; throws an AssertionError when it is empty. */
export function assertNonEmpty<T>(arr: readonly T[], msg?: string): NonEmpty<T> {
  if (!isNonEmpty(arr)) throw new AssertionError(msg ?? 'Expected a non-empty list');
  return arr;
}

/** Builds a NonEmpty from its elements. */
export function nonEmptyOf<T>(first: T, ...rest: T[]): NonEmpty<T> {
  return [first, ...rest];
}

/** Maps a NonEmpty to a NonEmpty. */
export function mapNonEmpty<T, U>(arr: NonEmpty<T>, fn: (item: T, index: number) => U): NonEmpty<U> {
  const [first, ...rest] = arr;
  const out: [U, ...U[]] = [fn(first, 0)];
  for (let i = 0; i < rest.length; i++) out.push(fn(rest[i] as T, i + 1));
  return out;
}

/** First element. */
export function headOf<T>(arr: NonEmpty<T>): T {
  return arr[0];
}

/** Last element. */
export function lastOf<T>(arr: NonEmpty<T>): T {
  return arr[arr.length - 1] as T;
}

/** Concatenation; the result is non-empty because the first list is. */
export function concatNonEmpty<T>(a: NonEmpty<T>, ...rest: ReadonlyArray<readonly T[]>): NonEmpty<T> {
  const out: [T, ...T[]] = [...a];
  for (const list of rest) out.push(...list);
  return out;
}

/** Appends one element to any list. */
export function appendNonEmpty<T>(arr: readonly T[], item: T): NonEmpty<T> {
  return [...arr, item] as unknown as NonEmpty<T>;
}

/** Filter; null when nothing passes. */
export function filterNonEmpty<T>(arr: readonly T[], pred: (item: T, index: number) => boolean): NonEmpty<T> | null {
  return toNonEmpty(arr.filter(pred));
}

/** Smallest / largest element by a numeric key (first one on ties). */
export function minBy<T>(arr: NonEmpty<T>, key: (item: T) => number): T {
  let best = arr[0];
  let bestKey = key(best);
  for (let i = 1; i < arr.length; i++) {
    const item = arr[i] as T;
    const k = key(item);
    if (k < bestKey) {
      best = item;
      bestKey = k;
    }
  }
  return best;
}

export function maxBy<T>(arr: NonEmpty<T>, key: (item: T) => number): T {
  let best = arr[0];
  let bestKey = key(best);
  for (let i = 1; i < arr.length; i++) {
    const item = arr[i] as T;
    const k = key(item);
    if (k > bestKey) {
      best = item;
      bestKey = k;
    }
  }
  return best;
}
